import { useState } from 'react';
import { Drawer } from '@/shared/ui/Drawer';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import {
  BUSINESS_TYPE_META,
  businessTypeMeta,
} from '@/shared/api/types/tenant';
import { useBusinessTypeMutation } from './useBusinessTypeMutation';

/**
 * Header дотор идэвхтэй company-ийн бизнесийн төрлийг солих pill switcher.
 * UI нь TenantSwitcher-тэй ижил хэв загвартай (`ts-switcher-*` class бүлэг).
 *
 * Backend (`PATCH /api/company/{id}`) нь BusinessType + RegisterNo + Email +
 * Phone-ыг бүхэлд нь дарж бичдэг тул mutation хийхэд эдгээр талбарыг хадгалж
 * үлдээх ёстой. Тиймээс current company-ийн object-ыг companies query cache-аас
 * уншиж, BusinessType-аас бусдыг нь өөрчлөлгүй дамжуулна.
 *
 * Permission: backend-ийн UpdateCompanyAsync нь tmCompanyUser.Role <= Admin
 * шаарддаг. Role гэхгүй хэрэглэгчид 403 буцна — toast-аар алдаа харуулна.
 */
export function BusinessTypeSwitcher() {
  const scope = useTenantScope();
  const [open, setOpen] = useState(false);
  const mut = useBusinessTypeMutation();

  // Scope бүрэн resolve болоогүй (companies query pending) бол harahgui.
  if (scope.status !== 'ok' || scope.companyBusinessType == null) return null;

  const current = businessTypeMeta(scope.companyBusinessType);
  const currentValue = current.value;

  function pick(meta: { value: number; enabled: boolean }) {
    // Disabled type (`enabled: false`) дээр дарвал юу ч хийхгүй — UI хараахан
    // хөгжөөгүй тул backend-руу ч явуулахгүй.
    if (!meta.enabled) return;
    if (meta.value === currentValue) {
      setOpen(false);
      return;
    }
    mut.mutate(meta.value, { onSuccess: () => setOpen(false) });
  }

  return (
    <>
      <button
        type="button"
        className="ts-switcher-trigger ts-switcher-biz"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        title={`Бизнесийн төрөл: ${current.label}`}
      >
        <i className={`bi ${current.icon} ts-switcher-icon`} />
        <span className="ts-switcher-text">
          <span className="ts-switcher-tenant">{current.label}</span>
          <span className="ts-switcher-company">
            <i className="bi bi-tag" /> Бизнесийн төрөл
          </span>
        </span>
        <i className="bi bi-chevron-down ts-switcher-chev" />
      </button>

      <Drawer
        open={open}
        onClose={() => (mut.isPending ? undefined : setOpen(false))}
        width="420px"
        title={
          <span>
            <i className="bi bi-tag mr-2" /> Бизнесийн төрөл сонгох
          </span>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {BUSINESS_TYPE_META.map((b) => {
            const active = b.value === currentValue;
            const disabled = !b.enabled || mut.isPending;
            return (
              <button
                key={b.value}
                type="button"
                className={`ts-switcher-item${active ? ' active' : ''}`}
                onClick={() => pick(b)}
                disabled={disabled}
                title={!b.enabled ? 'Тун удахгүй — UI хөгжиж байна' : undefined}
                style={!b.enabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <i className={`bi ${b.icon} ts-switcher-item-icon`} />
                <span className="ts-switcher-item-label">
                  {b.label}
                  {b.category === 'new' ? (
                    <span
                      className="ml-2"
                      style={{
                        fontSize: '0.6rem',
                        color: 'var(--ts-accent)',
                        fontWeight: 700,
                        marginLeft: 6,
                      }}
                    >
                      ШИНЭ
                    </span>
                  ) : null}
                  {!b.enabled ? (
                    <span
                      className="ml-2"
                      style={{
                        fontSize: '0.6rem',
                        color: 'var(--ts-muted)',
                        marginLeft: 6,
                      }}
                    >
                      Тун удахгүй
                    </span>
                  ) : null}
                </span>
                {active ? <i className="bi bi-check-lg ts-switcher-check" /> : null}
              </button>
            );
          })}
          {mut.isPending ? (
            <div className="ts-switcher-loading">
              <span className="spinner-border spinner-border-sm mr-2" /> Шинэчилж байна…
            </div>
          ) : null}
        </div>
      </Drawer>
    </>
  );
}
